import React, { useMemo } from "react";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import Styles from "@themes/Styles";

const StyledSelect = styled.select<{ blockCursor?: boolean; disabled?: boolean }>`
    cursor: ${(props) => (props.blockCursor ? "not-allowed" : "pointer")};
    color: ${(props) => (props.disabled ? props.theme.textColorSecondary : props.theme.textColorPrimary)};
    font-family: inherit;
    background-color: ${(props) => (props.disabled ? props.theme.disabledColor : props.theme.backgroundPrimary)};
    border-color: ${(props) => props.theme.borderPrimary};
    border-radius: ${Styles.borderRadiusSharp};
    padding: 4px;
    -webkit-appearance: none;
`;

const StyledOption = styled.option`
    color: inherit;
`;

const TOOLTIP_ID = "select-input-tooltip";

type AllowedSelectValue = string | number;

export interface Option<TValue extends AllowedSelectValue> {
    readonly label: string;
    readonly value: TValue;
}

interface LifeAtSelectInputProps<TValue extends AllowedSelectValue> {
    readonly id?: string;
    readonly options: readonly Option<TValue>[];
    readonly value?: TValue;
    readonly onChange: (value?: TValue) => void;
    readonly disabled?: boolean;
    readonly placeholder?: string;
    readonly blockCursor?: boolean;
    readonly tooltip?: string;
}

function LifeAtSelectInput<TValue extends AllowedSelectValue>({
    id,
    options,
    value,
    onChange,
    disabled = false,
    placeholder,
    tooltip,
}: LifeAtSelectInputProps<TValue>) {
    const optionsMap = useMemo(
        () => new Map(options.map((option) => [option.value.toString(), option.value])),
        [options],
    );

    const handleChange: React.ChangeEventHandler<HTMLSelectElement> = (e) => {
        if (e.target.value === "") {
            onChange(undefined);
        } else {
            const value = optionsMap.get(e.target.value);
            if (value == null) {
                console.warn("Selected value is not one of the options.");
                return;
            }
            onChange(value);
        }
    };

    return (
        <>
            <StyledSelect
                id={id}
                value={value ?? ""}
                onChange={handleChange}
                disabled={disabled}
                placeholder={placeholder}
                data-tip
                data-for={TOOLTIP_ID}
            >
                {placeholder && (
                    <StyledOption value="" disabled>
                        {placeholder}
                    </StyledOption>
                )}
                {options.map((option) => (
                    <StyledOption key={option.value} value={option.value}>
                        {option.label}
                    </StyledOption>
                ))}
            </StyledSelect>

            {tooltip && <ReactTooltip id={TOOLTIP_ID}>{tooltip}</ReactTooltip>}
        </>
    );
}
export default LifeAtSelectInput;
